import { atom } from "jotai";
import { atomWithImmer } from "jotai-immer";

import type {
  DataTableColumnFilter,
  DataTableFilterOption,
} from "@frontend/components/organisms/DataTable";
import { loadReferences } from "@frontend/lib/references";

import { clientApi } from "../services";
import type { ClientListQuery } from "../services";
import type {
  ClientListColumn,
  ClientListDirection,
  ClientListFilter,
  ClientPage,
  ClientRow,
} from "../types";

export type ClientListFilterOptions = {
  statuses: DataTableFilterOption[];
  types: DataTableFilterOption[];
  fields: DataTableFilterOption[];
  regions: DataTableFilterOption[];
};

export type ClientListState = {
  rows: ClientRow[];
  total: number;
  page: number;
  perPage: number;
  search: string;
  filter: ClientListFilter;
  sort: ClientListColumn | null;
  direction: ClientListDirection;
  criteria: DataTableColumnFilter[];
  filterOptions: ClientListFilterOptions;
  loading: boolean;
  error: string | null;
};

export const initialClientListState: ClientListState = {
  rows: [],
  total: 0,
  page: 1,
  perPage: 25,
  search: "",
  filter: "active",
  sort: null,
  direction: "asc",
  criteria: [],
  filterOptions: { statuses: [], types: [], fields: [], regions: [] },
  loading: true,
  error: null,
};

export const clientListAtom = atomWithImmer<ClientListState>(
  initialClientListState,
);

/** The last pages read, keyed on the query that asked for them. */
const PAGE_CACHE_LIMIT = 3;

type ClientListSession = {
  /** Numbers the reads: an answer to an older one is dropped. */
  draw: number;
  cache: Map<string, ClientPage>;
  inFlight: Map<string, Promise<ClientPage>>;
};

// Built per store: a list of its own remembers its own pages.
const clientListSessionAtom = atom<ClientListSession>(() => ({
  draw: 0,
  cache: new Map(),
  inFlight: new Map(),
}));

type ClientListRequest = Required<ClientListQuery>;

function toRequest(state: ClientListState): ClientListRequest {
  const { search, filter, page, perPage, sort, direction, criteria } = state;

  return { search, filter, page, perPage, sort, direction, criteria };
}

/**
 * One request, once: the page on its way in for a hover is the one a click
 * then waits on, rather than a second copy of it.
 */
function fetchPage(
  session: ClientListSession,
  query: ClientListRequest,
): Promise<ClientPage> {
  const key = JSON.stringify(query);
  const pending = session.inFlight.get(key);

  if (pending) {
    return pending;
  }

  const promise = clientApi
    .listClients(query)
    .then((answer) => {
      remember(session, key, answer);

      return answer;
    })
    .finally(() => session.inFlight.delete(key));

  session.inFlight.set(key, promise);

  return promise;
}

function remember(
  session: ClientListSession,
  key: string,
  answer: ClientPage,
): void {
  session.cache.delete(key);
  session.cache.set(key, answer);

  while (session.cache.size > PAGE_CACHE_LIMIT) {
    const oldest = session.cache.keys().next().value;

    if (undefined === oldest) {
      break;
    }

    session.cache.delete(oldest);
  }
}

function recall(
  session: ClientListSession,
  key: string,
): ClientPage | undefined {
  const answer = session.cache.get(key);

  if (answer) {
    // Read again means kept longer.
    session.cache.delete(key);
    session.cache.set(key, answer);
  }

  return answer;
}

export const loadClientListAtom = atom(
  null,
  async (get, set): Promise<void> => {
    const session = get(clientListSessionAtom);
    const query = toRequest(get(clientListAtom));
    const key = JSON.stringify(query);
    const draw = (session.draw += 1);

    const apply = (answer: ClientPage) => {
      if (draw !== session.draw) {
        return;
      }

      set(clientListAtom, (draft) => {
        draft.rows = answer.items;
        draft.total = answer.total;
        draft.page = answer.page;
        draft.perPage = answer.perPage;
        draft.loading = false;
        draft.error = null;
      });
    };

    const cached = recall(session, key);

    if (cached) {
      apply(cached);

      return;
    }

    set(clientListAtom, (draft) => {
      draft.loading = true;
      draft.error = null;
    });

    try {
      apply(await fetchPage(session, query));
    } catch (error: unknown) {
      if (draw !== session.draw) {
        return;
      }

      set(clientListAtom, (draft) => {
        draft.loading = false;
        draft.error = message(error);
      });
    }
  },
);

/** Reads the current page from the server again: a row changed under it. */
export const reloadClientListAtom = atom(
  null,
  async (get, set): Promise<void> => {
    get(clientListSessionAtom).cache.clear();

    await set(loadClientListAtom);
  },
);

/**
 * Reads a page ahead of the click, for the pointer resting on a page button or
 * a sortable header. It lands in the cache and nowhere else.
 */
export const prefetchClientListAtom = atom(
  null,
  (
    get,
    _set,
    query: Partial<Pick<ClientListState, "page" | "sort" | "direction">>,
  ): void => {
    const state = get(clientListAtom);
    const session = get(clientListSessionAtom);
    const request = { ...toRequest(state), ...query };
    const pageCount = Math.max(1, Math.ceil(state.total / state.perPage));

    if (request.page < 1 || request.page > pageCount) {
      return;
    }

    if (session.cache.has(JSON.stringify(request))) {
      return;
    }

    fetchPage(session, request).catch(() => {
      // The click that may follow asks again, and reports on its own.
    });
  },
);

/** Narrowing, filtering or reordering starts the listing from its first page. */
export const setClientListQueryAtom = atom(
  null,
  async (
    _get,
    set,
    query: Partial<
      Pick<
        ClientListState,
        | "search"
        | "filter"
        | "page"
        | "perPage"
        | "sort"
        | "direction"
        | "criteria"
      >
    >,
  ): Promise<void> => {
    set(clientListAtom, (draft) => {
      Object.assign(draft, query);

      if (undefined === query.page) {
        draft.page = 1;
      }
    });

    await set(loadClientListAtom);
  },
);

/** What the four picked-from-a-list column filters offer. */
export const loadClientListOptionsAtom = atom(
  null,
  async (_get, set): Promise<void> => {
    try {
      const [statuses, types, fields, regions] = await Promise.all([
        loadReferences("clientStatuses"),
        clientApi.fetchClientTypes(),
        loadReferences("clientFields"),
        loadReferences("clientRegions"),
      ]);

      set(clientListAtom, (draft) => {
        draft.filterOptions = {
          statuses: toOptions(statuses),
          types: toOptions(types),
          fields: toOptions(fields),
          regions: toOptions(regions),
        };
      });
    } catch {
      // The column filters stay empty, and the list itself still reads.
    }
  },
);

/**
 * Both row actions answer the same way: the page is read again, since a row
 * that left changes what the one after it is.
 */
export const archiveClientAtom = atom(
  null,
  async (_get, set, clientId: number): Promise<void> => {
    try {
      await clientApi.archiveClient(clientId);
    } catch (error: unknown) {
      set(clientListAtom, (draft) => {
        draft.error = message(error);
      });

      return;
    }

    await set(reloadClientListAtom);
  },
);

export const deleteClientAtom = atom(
  null,
  async (_get, set, clientId: number): Promise<void> => {
    try {
      await clientApi.deleteClient(clientId);
    } catch (error: unknown) {
      set(clientListAtom, (draft) => {
        draft.error = message(error);
      });

      return;
    }

    await set(reloadClientListAtom);
  },
);

/** The filter sends identifiers, which is what the API matches a picked column on. */
function toOptions(
  rows: { id: number; label: string }[],
): DataTableFilterOption[] {
  return rows.map(({ id, label }) => ({ value: String(id), label }));
}

function message(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}
